import { useEffect, useRef, useState } from "react";
import type { ExecutionHistory } from "../../../src/history/types";

/** A fixed insertion snapshot keeps live trades from moving older pages. */
export function useExecutionHistory(taskId: string, filters: string, liveVersion: unknown) {
  const key = `${taskId}?${filters}`;
  const [nav, setNav] = useState<{ key: string; cursors: Array<string | undefined>; index: number }>({ key, cursors: [undefined], index: 0 });
  const activeNav = nav.key === key ? nav : { key, cursors: [undefined], index: 0 };
  const cursor = activeNav.cursors[activeNav.index];
  const pageKey = `${key}&cursor=${cursor ?? ""}`;
  const snapshot = useRef<{ key: string; id?: number }>({ key });
  const [result, setResult] = useState<{ key: string; data: ExecutionHistory }>();
  const [error, setError] = useState<{ key: string; message: string }>();
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (snapshot.current.key !== key) snapshot.current = { key };
    const controller = new AbortController();
    const query = new URLSearchParams(filters);
    if (cursor) query.set("cursor", cursor);
    if (snapshot.current.id !== undefined) query.set("snapshot", String(snapshot.current.id));
    setLoading(true);
    setError(undefined);
    void (async () => {
      try {
        const response = await fetch(`/api/tasks/${encodeURIComponent(taskId)}/executions?${query}`, {
          credentials: "include", signal: controller.signal, cache: "no-store",
        });
        if (!response.ok) throw new Error(response.status === 401 ? "Session expired; sign in again" : "Could not load history. Refresh or reset the filters.");
        const data = await response.json() as ExecutionHistory;
        if (controller.signal.aborted) return;
        snapshot.current = { key, id: data.snapshot };
        setResult({ key: pageKey, data });
      } catch (err) {
        if (!controller.signal.aborted) setError({ key: pageKey, message: err instanceof Error ? err.message : "Could not load history" });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [taskId, filters, key, cursor, pageKey, version]);

  // Coalesce SSE bursts; polling also catches late DB-only reconciliation and
  // works for stopped campaigns that have no live task/SSE connection.
  useEffect(() => {
    const timer = setTimeout(() => setVersion(v => v + 1), 750);
    return () => clearTimeout(timer);
  }, [liveVersion]);
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setVersion(v => v + 1);
    }, 15_000);
    return () => clearInterval(timer);
  }, []);

  const data = result?.key === pageKey ? result.data : undefined;
  return {
    data, loading, error: error?.key === pageKey ? error.message : null,
    page: activeNav.index + 1,
    next: () => {
      if (!data?.nextCursor || loading) return;
      setNav({ key, cursors: [...activeNav.cursors.slice(0, activeNav.index + 1), data.nextCursor], index: activeNav.index + 1 });
    },
    previous: () => {
      if (activeNav.index > 0 && !loading) setNav({ ...activeNav, index: activeNav.index - 1 });
    },
    refresh: () => {
      snapshot.current = { key };
      setNav({ key, cursors: [undefined], index: 0 });
      setVersion(v => v + 1);
    },
  };
}
